import {
  publicRoute,
  localizedPath,
  PublicLocale,
  PUBLIC_LANGUAGE_KEY,
  supportedLocale,
} from './locales';
import type { GameKind } from '../games/types';

/** Public pages coexist with the reader's existing hash routes and callbacks. */
export const STUDY_URL = '/study/';
export const SIGNUP_URL = '/study/#/login?mode=signup';
export const LOGIN_URL = '/study/#/login';

export function isPublicHome(pathname: string, hash: string): boolean {
  return pathname === '/' && !hash.startsWith('#/');
}

export function isPublicPage(pathname: string, hash: string): boolean {
  pathname = publicRoute(pathname).path;
  return (
    isPublicHome(pathname, hash) ||
    resolveGameRoute(pathname) !== null ||
    /^\/blog(?:\/|$)/.test(pathname) ||
    /^\/(privacy-policy|terms-of-service|about|resources|contact|games|faq)\/?$/.test(pathname)
  );
}

/** Only the former FAQ hash routes leave the reader; other callbacks are untouched. */
export function isLegacyFaqRoute(pathname: string, hash: string): boolean {
  return ['/', '/study', '/study/'].includes(pathname) && /^#\/faq\/?(?:\?|$)/.test(hash);
}

/** Shared public paths and metadata; this module does not load gameplay. */
export const GAME_PAGES = {
  wordle: {
    path: '/games/wordle/',
    title: 'Bible Wordle',
    description:
      'Guess the daily five-letter Bible word in six attempts. Play as a guest or sign in to save your score and join the daily leaderboard.',
    image: '/games/wordle-illustration.webp',
  },
  blank: {
    path: '/games/fill-in-the-blank/',
    title: 'Fill in the blank',
    description:
      'Complete Bible verses in your chosen translation. Choose the missing word from three answers and practise the verses you missed.',
    image: '/games/blank-illustration.webp',
  },
  flashcards: {
    path: '/games/flashcards/',
    title: 'Verse flashcards',
    description:
      'Match Bible verses with their references in ten-card rounds. Choose your translation and one of three difficulty levels, then review missed cards.',
    image: '/games/flashcards-illustration.webp',
  },
} as const;

export function gamePath(kind: GameKind, leaderboard = false): string {
  return `${GAME_PAGES[kind].path}${leaderboard ? '?view=leaderboard' : ''}`;
}

export function resolveGameRoute(
  pathname: string,
  search = '',
): {
  kind: GameKind;
  leaderboardView: boolean;
} | null {
  const path = publicRoute(pathname).path.replace(/\/$/, '');
  const params = new URLSearchParams(search);
  const kind =
    (Object.keys(GAME_PAGES) as GameKind[]).find(
      (kind) => GAME_PAGES[kind].path.replace(/\/$/, '') === path,
    ) || (path === '/games' && params.get('game') === 'wordle' ? 'wordle' : null);
  return kind ? { kind, leaderboardView: params.get('view') === 'leaderboard' } : null;
}

/** Never interpolate a caller-supplied redirect into navigation. */
export function getLoginDestination(search: string): string {
  const params = new URLSearchParams(search);
  const destination = params.get('redirect');
  if (destination === 'subscribe') {
    return '/subscribe';
  }
  if (destination === 'profile') {
    return '/profile';
  }
  return '/';
}

/** Only this fixed public return can leave the reader after completed setup. */
export function completeLoginNavigation(
  search: string,
  navigate: (path: string) => void,
  openPublic = (path: string) => window.location.assign(path),
) {
  const params = new URLSearchParams(search);
  const locale = supportedLocale(params.get('lang')) || savedPublicLocale();
  if (params.get('redirect') === 'wordle-leaderboard') {
    openPublic(localizedPath(gamePath('wordle', true), locale));
  } else if (new URLSearchParams(search).get('redirect') === 'games') {
    openPublic(localizedPath(gamePath('wordle'), locale));
  } else {
    navigate(getLoginDestination(search));
  }
}

function savedPublicLocale(): PublicLocale {
  try {
    return supportedLocale(localStorage.getItem(PUBLIC_LANGUAGE_KEY)) || 'en';
  } catch {
    return 'en';
  }
}
